import React, { useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '@easyflow/ui-master/components/ui/button';
import { Input } from '@easyflow/ui-master/components/ui/input';
import { Label } from '@easyflow/ui-master/components/ui/label';
import { Alert, AlertDescription } from '@easyflow/ui-master/components/ui/alert';
import {
  Loader2, Shield, Lock, Eye, EyeOff, CheckCircle, AlertTriangle, ArrowLeft,
} from 'lucide-react';
import edgLogoWhite from '@/assets/edg-logo-white.png';
import { getCurrentAdminSurfaceContext } from '@/lib/paths';

// Task #3585 — Pagina reset password della console master, dual-mode
// (root sul sottodominio dedicato, /master/reset-password legacy sull'apex).
// Il link arriva via email da POST /api/master/users/:id/reset-password
// (admin-triggered) o /api/master/users/password/forgot (self-service);
// il completamento avviene su POST /api/master/reset-password (pubblico).

interface PasswordRule {
  label: string;
  test: (value: string) => boolean;
}

// Allineate 1:1 alla passwordPolicy condivisa (resetPasswordBodySchema).
const PASSWORD_RULES: PasswordRule[] = [
  { label: 'Almeno 8 caratteri', test: (v) => v.length >= 8 },
  { label: 'Una lettera maiuscola', test: (v) => /[A-Z]/.test(v) },
  { label: 'Una lettera minuscola', test: (v) => /[a-z]/.test(v) },
  { label: 'Un numero', test: (v) => /[0-9]/.test(v) },
  { label: 'Un carattere speciale', test: (v) => /[^A-Za-z0-9]/.test(v) },
];

interface ResetPasswordResponse {
  success: boolean;
  message?: string;
  error?: string;
}

export default function MasterResetPassword(): React.ReactElement {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const surfaceContext = getCurrentAdminSurfaceContext();
  const surfaceLabel = surfaceContext.surface === 'crm' ? 'EasyCRM' : 'Console Master';
  const token = searchParams.get('token');

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const failedRules = useMemo(
    () => PASSWORD_RULES.filter((rule) => !rule.test(password)),
    [password],
  );
  const passwordsMatch = password.length > 0 && password === confirmPassword;
  const canSubmit = Boolean(token) && failedRules.length === 0 && passwordsMatch && !submitting;

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    if (!canSubmit || !token) return;
    setSubmitting(true);
    setError(null);
    try {
      const response = await fetch('/api/master/reset-password', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, password }),
      });
      const data = (await response.json()) as ResetPasswordResponse;
      if (response.ok && data.success) {
        setSuccess(true);
      } else {
        setError(data.error || 'Impossibile reimpostare la password. Riprova.');
      }
    } catch {
      setError('Errore di rete. Riprova tra qualche istante.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-900 via-indigo-950 to-slate-900 px-4">
      <div className="w-full max-w-md">
        <div className="flex justify-center mb-8">
            <img src={edgLogoWhite} alt={surfaceLabel} className="h-10" />
        </div>
        <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-2xl p-8 border border-slate-200 dark:border-slate-800">
          <div className="flex items-center gap-3 mb-6">
            <div className="p-2 rounded-lg bg-indigo-500/10">
              <Shield className="h-6 w-6 text-indigo-600" />
            </div>
            <div>
              <h1 className="text-lg font-semibold text-slate-900 dark:text-slate-100">Reimposta password</h1>
              <p className="text-sm text-slate-500 dark:text-slate-400">{surfaceLabel}</p>
            </div>
          </div>

          {!token ? (
            <Alert variant="destructive">
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription>
                Link non valido: token di reset mancante. Richiedi un nuovo link.
              </AlertDescription>
            </Alert>
          ) : success ? (
            <div className="space-y-4">
              <Alert>
                <CheckCircle className="h-4 w-4 text-green-600" />
                <AlertDescription>
                  Password reimpostata con successo. Ora puoi effettuare il login.
                </AlertDescription>
              </Alert>
              <Button className="w-full" onClick={() => navigate(surfaceContext.loginPath)}>
                Vai al login
              </Button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              {error && (
                <Alert variant="destructive">
                  <AlertTriangle className="h-4 w-4" />
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}
              <div className="space-y-2">
                <Label htmlFor="new-password">Nuova password</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <Input
                    id="new-password"
                    type={showPassword ? 'text' : 'password'}
                    autoComplete="new-password"
                    className="pl-9 pr-10"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    disabled={submitting}
                  />
                  <button
                    type="button"
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                    onClick={() => setShowPassword((v) => !v)}
                    aria-label={showPassword ? 'Nascondi password' : 'Mostra password'}
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="confirm-password">Conferma password</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <Input
                    id="confirm-password"
                    type={showPassword ? 'text' : 'password'}
                    autoComplete="new-password"
                    className="pl-9"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    disabled={submitting}
                  />
                </div>
                {confirmPassword.length > 0 && !passwordsMatch && (
                  <p className="text-xs text-red-600">Le password non coincidono.</p>
                )}
              </div>
              {password.length > 0 && failedRules.length > 0 && (
                <ul className="text-xs text-slate-500 dark:text-slate-400 space-y-1">
                  {failedRules.map((rule) => (
                    <li key={rule.label}>• {rule.label}</li>
                  ))}
                </ul>
              )}
              <Button type="submit" className="w-full" disabled={!canSubmit}>
                {submitting ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Reimpostazione…
                  </>
                ) : (
                  'Reimposta password'
                )}
              </Button>
              <button
                type="button"
                className="w-full flex items-center justify-center gap-1 text-sm text-slate-500 hover:text-slate-700 dark:hover:text-slate-300"
                onClick={() => navigate(surfaceContext.loginPath)}
              >
                <ArrowLeft className="h-4 w-4" /> Torna al login
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
